import React, { useCallback } from 'react';
import { PMZoneSiteModel, PMZoneUnitsState } from '@shared/models';
import { Card, makeStyles, Theme, Typography } from '@material-ui/core';
import { sitePMZoneLabelsFn } from './SiteListItem.utils';
import { t, Trans } from '@lingui/macro';
import { SiteDotMarker } from '@features/PressView/sections/SitesMap/components/SiteDotMarker';
import { getSiteColor } from '@features/PressView/sections/SitesMap/components/Map.styles';
import { useAppDispatch, useAppSelector } from '@core/store/store';
import { usePmZoneSiteListDataSource } from '@features/dataSource';
import { Button, ConfirmButton, PromptType, useFormatDate, useNotifications } from '@hwm/ui-lib';
import { selectedAccountSelector } from '@core/store/library-stores';
import { pmZoneSitesReducer } from '@core/store/entity/pmzoneSites/PMZoneSitesReducer';
import { ADD_REMOVE_SITE_DELAY } from '../SitesList';
import { preferencesSelector } from '@core/store/entity/preferences/PreferencesSelectors';
import { accountsThunks } from '@core/store/library-stores/accounts.thunks';
import { parseStringWithDate } from '@shared/utils';

export const useStyles = makeStyles((theme: Theme) => ({
  card: {
    backgroundColor: '#F8F8F8',
  },
}));

interface SiteListItemProps {
  site: PMZoneSiteModel;
  click?: () => void;
  canRemove?: boolean;
  setLoading?: React.Dispatch<React.SetStateAction<boolean>>;
  /**
   * `dataTestId` - name of the Site List Item (preferably `<name>-site-<index>`)
   * @example
   * ```tsx
   * <SiteListItem site={site} dataTestId="test-site-0" />
   * ...
   * <SiteListItem site={site} dataTestId="test-site-1" />
   */
  dataTestId: string;
}

export const SiteListItem: React.FC<SiteListItemProps> = ({
  site,
  click,
  canRemove = false,
  setLoading,
  dataTestId,
}) => {
  const classes = useStyles();

  const dispatch = useAppDispatch();
  const { formatDate } = useFormatDate();
  const { handleError } = useNotifications();

  const { id } = useAppSelector(selectedAccountSelector);
  const { preferences: units } = useAppSelector(preferencesSelector);
  const { getPmZoneSites } = usePmZoneSiteListDataSource();

  const { siteName, siteType, isFlatLine, isPressureFault, isQuiet, isAssignedToPmZone, ...siteData } = site ?? {};

  const parseValue = useCallback(
    (key: string, val: string | number) => {
      if (val === undefined || val === null) {
        return t`No value`;
      }

      if (key === 'batteryLevel') {
        const valueInVolts = parseInt(val?.toString()) / 10;

        return `${valueInVolts}V`;
      }

      const value = typeof val === 'string' ? parseStringWithDate(val, formatDate) : val;

      if (key === 'lastCallIn' && value !== '-' && isQuiet) {
        const [date, rest] = (value as string)?.split(' [');

        return (
          <>
            <span className="text-red">{date}</span>
            {rest ? ` [${rest}` : ''}
          </>
        );
      }

      return value;
    },
    [isQuiet, formatDate]
  );

  const formattedSiteData = useCallback(
    (key: string, value: string | number) => {
      const parsedValue = parseValue(key, value);

      return key !== 'id' && key !== 'isAssignedToPmZone' && key !== 'isAssignedToOtherPmZone' ? (
        <div key={key} className="flex items-baseline mr-4" data-testid={`${dataTestId}-${key}`}>
          <Typography style={{ marginRight: 4 }} variant="subtitle1" data-testid={`${dataTestId}-${key}-label`}>
            <strong>{sitePMZoneLabelsFn()[key] ?? ''}:</strong>
          </Typography>
          <Typography variant="subtitle2" data-testid={`${dataTestId}-${key}-value`}>
            {parsedValue}
          </Typography>
        </div>
      ) : null;
    },
    [dataTestId, parseValue]
  );

  const removeSiteFromPMZone = useCallback(
    (accountId: number, siteId: number, units: PMZoneUnitsState) => {
      if (!accountId || !siteId) return;

      dispatch(
        pmZoneSitesReducer.thunks.removeSiteFromPMZone({
          accountId,
          siteId,
        })
      )
        .then(() => {
          setLoading(true);

          setTimeout(() => {
            getPmZoneSites({
              accountId: accountId.toString(),
            }).finally(() => {
              setLoading(false);
            });
            dispatch(accountsThunks.getPmZoneData({ accountId: accountId, units }));
          }, ADD_REMOVE_SITE_DELAY);
        })
        .catch(() => {
          handleError(t`There was an error while trying to remove site from PMZone`, t`PMZone`);
        });
    },
    [dispatch, getPmZoneSites, handleError, setLoading]
  );

  return (
    <Card className={`flex flex-col p-2 my-2 ml-0.5 'w-full ${classes.card}`} data-testid={`${dataTestId}-wrapper`}>
      <div className="flex flex-row items-center justify-between w-100">
        <Typography data-testid={`${dataTestId}-site-id`}>
          <strong className="mr-1">
            <Trans>Site ID</Trans>
            {': '}
          </strong>
          {siteName}
        </Typography>
        <div className="ml-3">
          <SiteDotMarker color={getSiteColor(site)} siteType={siteType} />
        </div>
      </div>
      <Typography style={{ width: '100%', marginRight: 4 }} data-testid={`${dataTestId}-device-type`}>
        <strong>
          <Trans>Device Type</Trans>
          {': '}
        </strong>
        {siteType}
      </Typography>
      <div className="flex flex-wrap mb-1">
        {Object.entries(siteData).map(([key, value]) =>
          value ? formattedSiteData(key, value as string | number) : null
        )}
      </div>
      <div className="flex justify-between">
        {click ? (
          <Button
            style={{ height: 28, width: 104, marginRight: 8 }}
            onClick={() => click()}
            dataTestId={`${dataTestId}-show-alarms-button`}
          >
            <Trans>Show Alarms</Trans>
          </Button>
        ) : (
          <div />
        )}
        {canRemove && (
          <ConfirmButton
            promptProps={{
              content: (
                <Typography>
                  {t`Do you want to remove a site ${site?.siteName ?? site?.address} from a PMZone?`}
                </Typography>
              ),
              title: t`Removing site from PMZone`,
              type: PromptType.Warning,
              confirmLabel: t`Remove`,
              onConfirm: () => removeSiteFromPMZone(id, site?.id, units),
            }}
            dataTestId={`${dataTestId}-remove-site-confirm-prompt`}
          >
            {({ onClick }) => (
              <Button
                variant="outlined"
                style={{ justifySelf: 'end', height: 28, width: 104 }}
                onClick={onClick}
                disabled={!canRemove}
                dataTestId={`${dataTestId}-remove-site-confirm-prompt-button`}
              >
                <Trans>Remove Site</Trans>
              </Button>
            )}
          </ConfirmButton>
        )}
      </div>
    </Card>
  );
};
